import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config';
import { copyToClipboard } from '../utils/clipboard';

const MODULE_LABELS = {
  allergen: 'Alerjen Mutfağı',
  diet: 'Diyet Mutfağı',
  athlete: 'Sporcu Mutfağı',
  alakart: 'Alakart Menü',
  special_days: 'Özel Günler Mutfağı',
  global: 'Global Premium'
};

const OPEN_STATUSES = ['pending_payment', 'receipt_uploaded', 'manual_review'];

function getAuthHeaders(extra = {}) {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}`, ...extra } : extra;
}

function formatTRY(value) {
  return Number(value || 0).toLocaleString('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  });
}

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('tr-TR');
}

function getStatusMeta(status) {
  switch (status) {
    case 'verified':
      return { label: 'Ödeme Onaylandı', text: 'Premium erişiminiz açıldı. Sayfayı yenileyebilirsiniz.' };
    case 'manual_review':
      return { label: 'Manuel Kontrol', text: 'Dekontunuz kuyruga alindi. Kisa sure icinde kontrol edilir.' };
    case 'receipt_uploaded':
      return { label: 'Dekont Yuklendi', text: 'Odemeniz kontrol ediliyor. Genelde birkac dakika icinde sonuc olusur.' };
    case 'failed':
      return { label: 'Yeni İşlem Gerekiyor', text: 'Bu ödeme niyeti artık otomatik ilerleyemiyor. Yeni niyet oluşturabilir veya destek alabilirsiniz.' };
    default:
      return { label: 'Odeme Bekleniyor', text: '3 adimda EFT odemenizi tamamlayin.' };
  }
}

function orderPlans(plans, moduleKey) {
  if (!moduleKey) return plans;
  const exact = [];
  const bundle = [];
  const other = [];
  plans.forEach((plan) => {
    const set = Array.isArray(plan.moduleSet) ? plan.moduleSet : [];
    if (set.includes(moduleKey) && set.length === 1) exact.push(plan);
    else if (set.includes(moduleKey)) bundle.push(plan);
    else other.push(plan);
  });
  return [...exact, ...bundle, ...other];
}

function formatPlanAccessLabel(durationMonths, moduleSet = []) {
  const numeric = Number(durationMonths || 0);
  if (Array.isArray(moduleSet) && moduleSet.length === 1 && moduleSet[0] === 'special_days') {
    return 'Tek kullanım erişimi';
  }
  return `${numeric} ay erişim`;
}

export default function PremiumPurchaseModal({ isOpen, onClose, preferredModuleKey, preferredModuleTitle, initialIntentId = null }) {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState(null);
  const [selectedPlanKey, setSelectedPlanKey] = useState('');
  const [intent, setIntent] = useState(null);
  const [intentLoading, setIntentLoading] = useState(false);
  const [intentError, setIntentError] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [senderName, setSenderName] = useState('');
  const [ocrText, setOcrText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [copiedField, setCopiedField] = useState('');
  const [openIntentReady, setOpenIntentReady] = useState(false);
  const [memberOptions, setMemberOptions] = useState([{ key: 'household', label: 'Hane geneli' }]);
  const [selectedTargetMemberKey, setSelectedTargetMemberKey] = useState('household');

  const orderedPlans = useMemo(() => orderPlans(plans, preferredModuleKey), [plans, preferredModuleKey]);
  const selectedPlan = orderedPlans.find((item) => item.planKey === selectedPlanKey) || orderedPlans[0];
  const statusMeta = useMemo(() => getStatusMeta(intent?.status), [intent?.status]);
  const selectedPlanSupportsMemberScope = useMemo(() => {
    const moduleSet = Array.isArray(selectedPlan?.moduleSet) ? selectedPlan.moduleSet : [];
    return moduleSet.includes('diet') || moduleSet.includes('athlete');
  }, [selectedPlan]);

  const refreshIntentStatus = useCallback(async () => {
    if (!intent?.id) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/payments/intents/${intent.id}/status`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.intent) setIntent(data.intent);
    } catch (error) {
      console.error('intent refresh error', error);
    }
  }, [intent?.id]);

  useEffect(() => {
    if (!selectedPlanSupportsMemberScope) {
      setSelectedTargetMemberKey('household');
    }
  }, [selectedPlanSupportsMemberScope]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/payments/my-open-intent`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        const data = await res.json().catch(() => ({}));
        if (mounted && res.ok) setIntent(data.intent || null);
      } catch (error) {
        console.error('open intent error', error);
      } finally {
        if (mounted) setOpenIntentReady(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/profile`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        const data = await res.json().catch(() => ({}));
        if (!mounted || !res.ok) return;

        const options = [{ key: 'household', label: 'Hane geneli' }];
        const userName = data?.user?.name || data?.name || 'Ben';
        options.push({ key: 'me', label: userName });

        if (data?.partnerId) {
          options.push({ key: 'partner', label: data?.partnerName || 'Eş/Partner' });
        }

        const children = Array.isArray(data?.children)
          ? data.children
          : (typeof data?.children === 'string' ? JSON.parse(data.children || '[]') : []);
        children.forEach((child, idx) => {
          options.push({ key: `child_${idx}`, label: child?.name || `Çocuk ${idx + 1}` });
        });

        setMemberOptions(options);
      } catch (error) {
        // Profil okunamasa bile ödeme akışı household modunda devam etsin.
        console.warn('premium modal profile load warning', error);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!isOpen || !initialIntentId) return;
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/payments/intents/${initialIntentId}/status`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        const data = await res.json().catch(() => ({}));
        if (mounted && res.ok && data.intent) {
          setIntent(data.intent);
          setSelectedTargetMemberKey(data.intent.targetMemberKey || 'household');
        }
      } catch (error) {
        console.error('initial intent load error', error);
      }
    })();
    return () => { mounted = false; };
  }, [isOpen, initialIntentId]);

  useEffect(() => {
    if (!intent) return;
    setSelectedTargetMemberKey(intent.targetMemberKey || 'household');
  }, [intent?.id, intent?.targetMemberKey]);

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;
    (async () => {
      try {
        setPlansLoading(true);
        setPlansError(null);
        const moduleQuery = preferredModuleKey ? `?moduleKey=${encodeURIComponent(preferredModuleKey)}` : '';
        const res = await fetch(`${API_BASE_URL}/api/premium/plans${moduleQuery}`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'plans_error');
        if (!mounted) return;
        const nextPlans = Array.isArray(data.plans) ? data.plans : [];
        setPlans(nextPlans);
        if (!selectedPlanKey && nextPlans.length > 0) {
          const ordered = orderPlans(nextPlans, preferredModuleKey);
          setSelectedPlanKey(ordered[0]?.planKey || nextPlans[0]?.planKey || '');
        }
      } catch (error) {
        console.error('plans error', error);
        if (mounted) setPlansError('Fiyat planlari simdilik yuklenemedi.');
      } finally {
        if (mounted) setPlansLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [isOpen, preferredModuleKey, selectedPlanKey]);

  useEffect(() => {
    if (!isOpen || !intent?.id || !OPEN_STATUSES.includes(intent.status)) return undefined;
    const timer = window.setInterval(() => {
      refreshIntentStatus();
    }, 45000);
    return () => window.clearInterval(timer);
  }, [isOpen, intent?.id, intent?.status, refreshIntentStatus]);

  useEffect(() => {
    const handleRealtimePayment = () => {
      refreshIntentStatus();
    };
    window.addEventListener('realtime:payment-updated', handleRealtimePayment);
    window.addEventListener('realtime:force-refetch', handleRealtimePayment);
    return () => {
      window.removeEventListener('realtime:payment-updated', handleRealtimePayment);
      window.removeEventListener('realtime:force-refetch', handleRealtimePayment);
    };
  }, [refreshIntentStatus]);

  const handleCopy = async (value, field) => {
    const ok = await copyToClipboard(value);
    if (!ok) return;
    setCopiedField(field);
    window.setTimeout(() => setCopiedField(''), 1600);
  };

  const handleCreateIntent = async () => {
    if (!selectedPlanKey) return;
    try {
      setIntentLoading(true);
      setIntentError(null);
      const res = await fetch(`${API_BASE_URL}/api/payments/intents`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          planKey: selectedPlanKey,
          targetMemberKey: selectedTargetMemberKey || 'household'
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || 'intent_error');
      setIntent(data.intent || null);
      setShowUpload(false);
      setReceiptFile(null);
    } catch (error) {
      console.error('create intent error', error);
      setIntentError('Ödeme niyeti oluşturulamadı.');
    } finally {
      setIntentLoading(false);
    }
  };

  const handleCancelIntent = async () => {
    if (!intent?.id) return;
    try {
      setIntentLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/payments/intents/${intent.id}/cancel`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'cancel_error');
      setIntent(null);
      setShowUpload(false);
      setUploadError(null);
    } catch (error) {
      console.error('cancel intent error', error);
      setIntentError('Bekleyen ödeme iptal edilemedi.');
    } finally {
      setIntentLoading(false);
    }
  };

  const handleReceiptUpload = async () => {
    if (!intent?.id || !receiptFile) {
      setUploadError('Lütfen önce dekont dosyasını seç.');
      return;
    }
    try {
      setUploading(true);
      setUploadError(null);
      const formData = new FormData();
      formData.append('receipt', receiptFile);
      if (senderName.trim()) formData.append('senderName', senderName.trim());
      if (ocrText.trim()) formData.append('ocrText', ocrText.trim());
      const res = await fetch(`${API_BASE_URL}/api/payments/intents/${intent.id}/receipt`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || 'upload_error');
      setIntent(data?.processed?.intent || intent);
      setShowUpload(false);
      setReceiptFile(null);
    } catch (error) {
      console.error('receipt upload error', error);
      setUploadError('Dekont yüklenemedi.');
    } finally {
      setUploading(false);
    }
  };

  const showFloatingReminder = openIntentReady && !isOpen && intent?.id && OPEN_STATUSES.includes(intent.status);

  if (!isOpen && !showFloatingReminder) return null;

  return (
    <>
      {showFloatingReminder ? (
        <button
          type="button"
          onClick={() => onClose?.('open')}
          className="fixed bottom-6 right-6 z-[90] rounded-full bg-slate-900 px-5 py-3 text-sm font-black text-white shadow-2xl dark:bg-white dark:text-slate-900"
        >
          Ödeme Yaptım
        </button>
      ) : null}

      {isOpen ? (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <div className="relative max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-[#0f1722]">
            <button type="button" onClick={() => onClose?.()} className="absolute right-5 top-5 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              <span className="material-symbols-outlined">close</span>
            </button>
            <div className="grid max-h-[92vh] grid-cols-1 overflow-y-auto lg:grid-cols-[1.15fr_0.85fr]">
              <div className="border-b border-slate-200 p-6 dark:border-slate-800 lg:border-b-0 lg:border-r lg:p-8">
                <p className="text-[11px] font-black uppercase tracking-[0.24em] text-primary/80">Premium Keşfet</p>
                <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-900 dark:text-white">
                  {preferredModuleTitle || MODULE_LABELS[preferredModuleKey] || 'Premium Paketler'}
                </h2>
                <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
                  1. IBAN'a gönder 2. Kodu ekle 3. Dekontu yükle. Süreç uzun değil, görev gibi düşün.
                </p>

                <div className="my-6 grid grid-cols-3 gap-3 rounded-2xl bg-slate-100 p-3 dark:bg-slate-900">
                  {['IBAN', 'Kod', 'Dekont'].map((label, index) => {
                    const activeIndex = intent ? (showUpload || intent.status !== 'pending_payment' ? 2 : 1) : 0;
                    const active = index <= activeIndex;
                    return (
                      <div key={label} className={`rounded-2xl border px-3 py-3 text-center text-xs font-bold ${active ? 'border-primary/30 bg-white text-slate-900 dark:border-primary/20 dark:bg-slate-800 dark:text-white' : 'border-transparent text-slate-400'}`}>
                        <div className="mb-1 text-[10px] uppercase tracking-widest">Adım {index + 1}</div>
                        {label}
                      </div>
                    );
                  })}
                </div>

                {!intent ? (
                  <>
                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white">Fiyat Listesi</h3>
                      {plansLoading ? <span className="text-xs text-slate-400">Yükleniyor...</span> : null}
                    </div>
                    {plansError ? <p className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">{plansError}</p> : null}
                    {orderedPlans.length === 0 && !plansLoading && !plansError ? (
                      <div className="rounded-[1.5rem] border border-amber-200 bg-amber-50 px-5 py-5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
                        <div className="text-base font-black text-slate-900 dark:text-white">Henüz fiyat planı tanımlanmamış</div>
                        <p className="mt-2 leading-6">
                          Bu yuzden burada satilacak paket goremiyorsunuz. Once admin panelindeki
                          <span className="mx-1 font-black">Abonelikler &gt; Fiyat Planlari</span>
                          tabinda planlari olusturmak gerekiyor.
                        </p>
                        <p className="mt-2 leading-6">
                          Planlar tanımlandığında bu alan otomatik olarak kartlara dolacak ve kullanıcı ödeme niyeti oluşturabilecek.
                        </p>
                      </div>
                    ) : (
                      <div className="grid gap-4 md:grid-cols-2">
                        {orderedPlans.map((plan) => {
                          const highlighted = plan.planKey === selectedPlanKey;
                          const recommended = Array.isArray(plan.moduleSet) && plan.moduleSet.includes(preferredModuleKey);
                          return (
                            <button key={plan.planKey} type="button" onClick={() => setSelectedPlanKey(plan.planKey)} className={`rounded-[1.5rem] border p-5 text-left transition ${highlighted ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10 dark:border-primary/40 dark:bg-primary/10' : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/70'}`}>
                              <div className="mb-3 flex items-start justify-between gap-3">
                                <div>
                                  <h4 className="text-base font-black text-slate-900 dark:text-white">{plan.title}</h4>
                                  <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{plan.description}</p>
                                </div>
                                {recommended ? <span className="rounded-full bg-emerald-100 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">Önerilen</span> : null}
                              </div>
                              <div className="mb-3 flex flex-wrap gap-2">
                                {(plan.moduleSet || []).map((moduleKey) => <span key={`${plan.planKey}:${moduleKey}`} className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">{MODULE_LABELS[moduleKey] || moduleKey}</span>)}
                              </div>
                              <div className="flex items-end justify-between">
                                <div>
                                  <div className="text-2xl font-black text-slate-900 dark:text-white">{formatTRY(plan.amountTRY)}</div>
                                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400">{formatPlanAccessLabel(plan.durationMonths, plan.moduleSet)}</div>
                                </div>
                                {highlighted ? <span className="material-symbols-outlined text-primary">check_circle</span> : null}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
                      <div className="mb-2 text-xs font-black uppercase tracking-[0.2em] text-slate-400">Erişim Hedefi</div>
                      <select
                        value={selectedTargetMemberKey}
                        onChange={(e) => setSelectedTargetMemberKey(e.target.value)}
                        disabled={!selectedPlanSupportsMemberScope}
                        className="h-[44px] w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                      >
                        {memberOptions.map((item) => (
                          <option key={item.key} value={item.key}>{item.label}</option>
                        ))}
                      </select>
                      <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                        {selectedPlanSupportsMemberScope
                          ? 'Diyet ve Sporcu mutfağı için erişimi hangi aile üyesi adına açmak istediğinizi seçebilirsiniz.'
                          : 'Bu planda üye bazlı erişim yok; lisans hane geneli açılır.'}
                      </p>
                    </div>
                    <div className="mt-6 flex flex-col gap-3 border-t border-slate-200 pt-6 dark:border-slate-800 md:flex-row md:items-center md:justify-between">
                      <p className="text-sm text-slate-500 dark:text-slate-400">Tutar oluşturulan ödeme niyetinde kilitlenir. Sonradan admin panelindeki fiyat değişse bile bu niyet etkilenmez.</p>
                      <button type="button" onClick={handleCreateIntent} disabled={!selectedPlan || intentLoading || orderedPlans.length === 0} className="rounded-full bg-slate-900 px-6 py-3 text-sm font-black text-white disabled:opacity-60 dark:bg-white dark:text-slate-900">
                        {intentLoading ? 'Hazırlanıyor...' : 'Ödeme Kodunu Oluştur'}
                      </button>
                    </div>
                    {intentError ? <p className="mt-3 text-sm text-rose-600 dark:text-rose-300">{intentError}</p> : null}
                  </>
                ) : (
                  <>
                    <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm dark:border-slate-800 dark:bg-slate-900/60">
                      <div className="font-black text-slate-900 dark:text-white">{statusMeta.label}</div>
                      <div className="mt-1 leading-6 text-slate-500 dark:text-slate-400">{statusMeta.text}</div>
                    </div>
                    <div className="grid gap-4">
                      <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900/70">
                        <div className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-400">1. Bu IBAN'a gonder</div>
                        <div className="mt-3 text-lg font-black tracking-wide text-slate-900 dark:text-white">{intent.ibanSnapshot}</div>
                        <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">{intent.accountHolderSnapshot}</div>
                        <button type="button" onClick={() => handleCopy(intent.ibanSnapshot, 'iban')} className="mt-4 rounded-full bg-white px-4 py-2 text-xs font-black text-slate-700 dark:bg-slate-800 dark:text-slate-100">{copiedField === 'iban' ? 'IBAN Kopyalandi' : 'IBAN Kopyala'}</button>
                      </div>
                      <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900/70">
                        <div className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-400">2. Aciklamaya bunu yaz</div>
                        <div className="mt-3 inline-flex rounded-2xl border border-dashed border-primary/40 bg-primary/5 px-5 py-3 text-2xl font-black tracking-[0.22em] text-primary dark:border-primary/30 dark:bg-primary/10">{intent.intentCode}</div>
                        <button type="button" onClick={() => handleCopy(intent.intentCode, 'code')} className="mt-4 block rounded-full bg-white px-4 py-2 text-xs font-black text-slate-700 dark:bg-slate-800 dark:text-slate-100">{copiedField === 'code' ? 'Kod Kopyalandı' : 'Kodu Kopyala'}</button>
                      </div>
                      <div className="rounded-[1.5rem] border border-amber-200 bg-amber-50 p-5 dark:border-amber-500/20 dark:bg-amber-500/10">
                        <div className="text-[11px] font-black uppercase tracking-[0.24em] text-amber-700 dark:text-amber-300">3. Tam net tutari gonder</div>
                        <div className="mt-3 text-3xl font-black text-slate-900 dark:text-white">{formatTRY(intent.amountTRY)}</div>
                        <p className="mt-3 text-sm leading-6 text-amber-800 dark:text-amber-200">Masraflar haric bu tutarin net olarak hesabimiza gecmesi gerekir. Gerekirse masrafsiz gonderim secin.</p>
                      </div>
                    </div>
                    <div className="mt-6 flex flex-wrap gap-3">
                      <button type="button" onClick={() => setShowUpload((prev) => !prev)} className="rounded-full bg-primary px-5 py-3 text-sm font-black text-white">
                        {showUpload ? 'Dekont Alanını Kapat' : 'Ödeme Yaptım'}
                      </button>
                      {(intent.status === 'manual_review' || intent.status === 'failed') ? (
                        <button type="button" onClick={() => setShowUpload(true)} className="rounded-full border border-primary/30 px-5 py-3 text-sm font-bold text-primary dark:border-primary/40">
                          Dekontu Yeniden Yükle
                        </button>
                      ) : null}
                      <button type="button" onClick={handleCancelIntent} disabled={intentLoading} className="rounded-full border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 dark:border-slate-700 dark:text-slate-200">
                        Farkli Plan Sec
                      </button>
                      {intent.status === 'verified' ? <button type="button" onClick={() => window.location.reload()} className="rounded-full border border-emerald-300 px-5 py-3 text-sm font-bold text-emerald-700 dark:border-emerald-500/30 dark:text-emerald-300">Sayfayi Yenile</button> : null}
                    </div>
                    <p className="mt-4 text-xs leading-6 text-slate-500 dark:text-slate-400">Ödemeniz doğrulanmadan erişim açılmaz. Bekleyen ödemeniz olsa bile uygulamadan çıkarsanız bu niyet kaybolmaz.</p>
                    {showUpload ? (
                      <div className="mt-6 rounded-[1.75rem] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900/80">
                        <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">Ödemenizi yaptıysanız şimdi dekontu yükleyin. PDF dekonttan metin kopyalayabiliyorsanız alttaki alan otomatik kontrolü hızlandırır.</div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <div>
                            <label className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200">Dekont Dosyasi</label>
                            <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} className="block w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200" />
                          </div>
                          <div>
                            <label className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200">Gonderici Adi (opsiyonel)</label>
                            <input type="text" value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Dekonttaki ad soyad" className="block w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200" />
                          </div>
                        </div>
                        <div className="mt-4">
                          <label className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200">Dekont Metni (opsiyonel ama onerilir)</label>
                          <textarea rows={5} value={ocrText} onChange={(e) => setOcrText(e.target.value)} placeholder="PDF dekotttan kopyalayabildigin metni buraya yapistirabilirsin." className="block w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200" />
                        </div>
                        {uploadError ? <p className="mt-3 text-sm text-rose-600 dark:text-rose-300">{uploadError}</p> : null}
                        <div className="mt-5 flex flex-wrap gap-3">
                          <button type="button" onClick={handleReceiptUpload} disabled={uploading} className="rounded-full bg-slate-900 px-5 py-3 text-sm font-black text-white disabled:opacity-60 dark:bg-white dark:text-slate-900">{uploading ? 'Yükleniyor...' : 'Dekontu Gönder'}</button>
                          <button type="button" onClick={() => setShowUpload(false)} className="rounded-full border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 dark:border-slate-700 dark:text-slate-200">Vazgec</button>
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
              <div className="p-6 lg:p-8">
                <div className="rounded-[1.75rem] border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 dark:border-slate-800 dark:from-slate-900 dark:to-slate-950">
                  <div className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-400">Ödeme Özeti</div>
                  <h3 className="mt-3 text-xl font-black text-slate-900 dark:text-white">{intent ? 'Bekleyen EFT Ödemeniz' : 'Seçili Premium Planı'}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">Bu ekran form degil, gorev ekrani gibi calisir. Kullaniciya sadece ne yapmasi gerektigini net gostermek icin tasarlandi.</p>
                  <div className="mt-6 space-y-4">
                    <div className="rounded-2xl bg-white px-4 py-4 shadow-sm dark:bg-slate-900"><div className="text-xs font-bold uppercase tracking-widest text-slate-400">Paket</div><div className="mt-1 text-base font-black text-slate-900 dark:text-white">{intent ? intent.planTitle || intent.planKey : selectedPlan?.title || '-'}</div></div>
                    <div className="rounded-2xl bg-white px-4 py-4 shadow-sm dark:bg-slate-900"><div className="text-xs font-bold uppercase tracking-widest text-slate-400">Hedef Üye</div><div className="mt-1 text-base font-black text-slate-900 dark:text-white">{memberOptions.find((item) => item.key === (intent?.targetMemberKey || selectedTargetMemberKey))?.label || (intent?.targetMemberKey || selectedTargetMemberKey || 'Hane geneli')}</div></div>
                    <div className="rounded-2xl bg-white px-4 py-4 shadow-sm dark:bg-slate-900"><div className="text-xs font-bold uppercase tracking-widest text-slate-400">Süre</div><div className="mt-1 text-base font-black text-slate-900 dark:text-white">{intent ? formatPlanAccessLabel(intent.durationMonths, intent.moduleSet) : selectedPlan ? formatPlanAccessLabel(selectedPlan.durationMonths, selectedPlan.moduleSet) : '-'}</div></div>
                    <div className="rounded-2xl bg-white px-4 py-4 shadow-sm dark:bg-slate-900"><div className="text-xs font-bold uppercase tracking-widest text-slate-400">Tutar</div><div className="mt-1 text-base font-black text-slate-900 dark:text-white">{intent ? formatTRY(intent.amountTRY) : selectedPlan ? formatTRY(selectedPlan.amountTRY) : '-'}</div></div>
                    <div className="rounded-2xl bg-white px-4 py-4 shadow-sm dark:bg-slate-900"><div className="text-xs font-bold uppercase tracking-widest text-slate-400">Son Geçerlilik</div><div className="mt-1 text-base font-black text-slate-900 dark:text-white">{intent ? formatDateTime(intent.expiresAt) : 'Intent oluşturulduğunda yazılacak'}</div></div>
                  </div>
                  <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">
                    <div className="font-black text-slate-900 dark:text-white">Guvenceli akis</div>
                    <ul className="mt-3 space-y-2 leading-6">
                      <li>Ödemeniz doğrulanmadan premium erişim açılmaz.</li>
                      <li>Kod yanlış yazılsa bile sistem kırılmaz, manuel kontrole düşer.</li>
                      <li>Bekleyen ödemeniz uygulamadan çıksanız bile unutulmaz.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
